import type {FC} from 'react'
import {useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {sendUsageEventOnUnload, useUsageEventMutation} from '@apis/usage'
import type {EventInterface} from '@interfaces/usage/event'
import {ALPINIST_STORAGE_KEYS} from '@shared/hooks/useAlpinistUrlSync'

export const MIN_DURATION_MS = 1000 // visits shorter than this are not recorded
export const INACTIVITY_THRESHOLD_MS = 30000 // idle time after the last interaction that still counts

const ACTIVITY_EVENTS = ['mousemove', 'pointerdown', 'keydown', 'scroll'] as const

const isUsageTrackingEnabled = (): boolean => {
  if (import.meta.env.DEV) {
    return false
  }

  return import.meta.env.VITE_APP_ENABLE_USAGE_TRACKING !== 'false'
}

const getAlpinistProperties = (): {alpinist_open: number; alpinist_conversation: string} => {
  const isOpen = sessionStorage.getItem(ALPINIST_STORAGE_KEYS.open) === 'true'
  const conversationId = sessionStorage.getItem(ALPINIST_STORAGE_KEYS.conversation) || ''
  return {
    alpinist_open: isOpen ? 1 : 0,
    alpinist_conversation: conversationId,
  }
}

/**
 * Records one page_view per route (after MIN_DURATION_MS) and one page_exit with the
 * active duration when the route changes or the page is closed. Active time runs to
 * the last interaction plus INACTIVITY_THRESHOLD_MS, so an idle tab stops counting.
 */
const GlobalUsageTracker: FC = (): null => {
  const location = useLocation()
  const [sendUsageEvent] = useUsageEventMutation()

  useEffect(() => {
    if (!isUsageTrackingEnabled()) {
      return
    }

    let startedAt = Date.now()
    let lastActivityAt = startedAt
    let exitSent = false

    const properties = (): EventInterface['properties'] => ({
      host: window.location.hostname,
      page: location.pathname,
      search_params: location.search,
      timestamp: new Date().toISOString(),
      ...getAlpinistProperties(),
    })

    const pageViewTimeout = setTimeout((): void => {
      void sendUsageEvent({event_name: 'pm.core.usage.page_view', properties: properties()})
    }, MIN_DURATION_MS)

    const markActive = (): void => {
      lastActivityAt = Date.now()
    }

    const sendPageExit = (onUnload: boolean): void => {
      if (exitSent) return
      const activeUntil = Math.min(Date.now(), lastActivityAt + INACTIVITY_THRESHOLD_MS)
      const activeDuration = activeUntil - startedAt
      if (activeDuration < MIN_DURATION_MS) return

      exitSent = true
      const event: EventInterface = {
        event_name: 'pm.core.usage.page_exit',
        properties: {...properties(), duration: activeDuration / 1000},
      }
      if (onUnload) {
        sendUsageEventOnUnload(event)
      } else {
        void sendUsageEvent(event)
      }
    }

    const onPageHide = (): void => sendPageExit(true)
    // Restored from the back/forward cache: the same route becomes a fresh visit.
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return
      startedAt = Date.now()
      lastActivityAt = startedAt
      exitSent = false
    }

    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, markActive, {passive: true, capture: true}),
    )
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)

    return (): void => {
      clearTimeout(pageViewTimeout)
      sendPageExit(false)
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, markActive, {capture: true}),
      )
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [location.pathname, location.search, sendUsageEvent])

  return null // No UI, it's purely for tracking
}

export default GlobalUsageTracker
